import { useTranslate } from '@tolgee/react'
import React, {
  memo,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { useInView } from 'react-intersection-observer'

import Column from '@/components/common/Column'
import {
  ChevronDownIcon,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'oddle-shop-monorepo.design-system'

import HorizontalNavigation from './HorizontalNavigation'
import NotificationBanner from './NotificationBanner'
import VerticalNavigation from './VerticalNavigation'
import { P } from '@/components/common/Typo'

const desktopHeight = 80

interface CategoryVisibilityEmitter {
  emit: (
    event: string,
    data: { isVisible: boolean; category: Oms.MenuCategory }
  ) => void
  off: (
    event: string,
    callback: (data: { isVisible: boolean; category: Oms.MenuCategory }) => void
  ) => void
  on: (
    event: string,
    callback: (data: { isVisible: boolean; category: Oms.MenuCategory }) => void
  ) => void
  once: (
    event: string,
    callback: (data: { isVisible: boolean; category: Oms.MenuCategory }) => void
  ) => void
}

interface CategoriesNavigationWrapperProps {
  children: ({ selectedItemId }: { selectedItemId: string }) => JSX.Element
  categories?: Oms.MenuCategory[]
  categoryVisibilityEmitter: CategoryVisibilityEmitter
}

interface CategoriesNavigationProps {
  categoryVisibilityEmitter: CategoryVisibilityEmitter
  onClickItem: (e: React.MouseEvent<HTMLAnchorElement>, catId: string) => void
  showHorizontal?: boolean
  hideCategoriesNavigation: boolean
  productSectionSize: number
  syncScroll: boolean
  categories?: Oms.MenuCategory[]
  children: React.ReactNode
  showHorizontalNav: boolean
}

const appendHrefs = (categories: Oms.MenuCategory[]) =>
  categories.map((cat) => ({
    id: cat.id,
    name: cat.name || '',
    href: `/#${cat.id}`,
  }))

const CategoriesNavigationWrapper = memo(
  ({
    categoryVisibilityEmitter,
    categories = [],
    children,
  }: CategoriesNavigationWrapperProps): ReactNode => {
    const categoryVisibilitiesRef = useRef<Record<string, boolean>>({})
    const [selectedItemId, setSelectedItemId] = useState<string>('')
    const reversedCategories = useMemo(
      () => [...categories].reverse(),
      [categories]
    )

    const onVisibilityChange = useCallback(
      ({
        isVisible,
        category,
      }: {
        isVisible: boolean
        category: Oms.MenuCategory
      }) => {
        categoryVisibilitiesRef.current[category.id] = isVisible
        const firstVisibleCategory = reversedCategories.find(
          (cat) => categoryVisibilitiesRef.current[cat.id]
        )
        if (firstVisibleCategory?.id !== selectedItemId) {
          setSelectedItemId(firstVisibleCategory ? firstVisibleCategory.id : '')
        }
      },
      [reversedCategories, selectedItemId]
    )

    useEffect(() => {
      categoryVisibilityEmitter.on('visibilityChange', onVisibilityChange)
      return () => {
        categoryVisibilityEmitter.off('visibilityChange', onVisibilityChange)
      }
    }, [categoryVisibilityEmitter, onVisibilityChange])

    return children({ selectedItemId })
  }
)

CategoriesNavigationWrapper.displayName = 'CategoriesNavigationWrapper'

const CategoriesNavigation = memo(
  ({
    categoryVisibilityEmitter,
    onClickItem,
    hideCategoriesNavigation,
    showHorizontalNav,
    categories = [],
    productSectionSize,
    children,
    syncScroll,
  }: CategoriesNavigationProps): ReactNode => {
    const { t } = useTranslate()
    const [isOpenDropdown, setIsOpenDropdown] = useState(false)
    const [stuck, setStuck] = useState<boolean | null>(null)
    const prevStuckRef = useRef<boolean | null>(null)
    const { ref: inViewRef, inView } = useInView({
      rootMargin: '0px 0px 30px 0px',
      threshold: 0,
      initialInView: true,
    })

    // Add new useInView hook to track Products section
    const { ref: productsRef, inView: isProductsInView } = useInView({
      rootMargin: '-100% 0px 0px 0px', // Track when Products section is completely out of view from the top
      threshold: 0,
    })

    const topOffset = useMemo(
      () => (showHorizontalNav ? desktopHeight : desktopHeight + 24),
      [showHorizontalNav]
    )

    const items = useMemo(() => appendHrefs(categories), [categories])

    const handleItemClick = useCallback(
      (e: React.MouseEvent<HTMLAnchorElement>, itemId: string) => {
        onClickItem(e, itemId)
      },
      [onClickItem]
    )

    const setRefs = useCallback(
      (node: HTMLDivElement | null) => {
        inViewRef(node)
      },
      [inViewRef]
    )

    useEffect(() => {
      const handleScroll = () => {
        const firstCategory = document.getElementById('category-0')

        // Only set stuck to true if we're still within the Products section
        const shouldBeStuck =
          (firstCategory?.getBoundingClientRect()?.y || 1) < 0

        if (prevStuckRef.current !== shouldBeStuck) {
          prevStuckRef.current = shouldBeStuck
          setStuck(shouldBeStuck)
        }
      }

      window.addEventListener('scroll', handleScroll)
      handleScroll() // Initial check

      return () => {
        window.removeEventListener('scroll', handleScroll)
      }
    }, [inView, isProductsInView])

    const navWrapperProps = useMemo(
      () => ({
        categories,
        categoryVisibilityEmitter,
      }),
      [categories, categoryVisibilityEmitter]
    )

    const horizontalNavProps = useMemo(
      () => ({
        items,
        onClickItem: handleItemClick,
      }),
      [items, handleItemClick]
    )

    const verticalNavProps = useMemo(
      () => ({
        syncScroll,
        items,
        onClickItem: handleItemClick,
        stickyOffset: 90,
      }),
      [syncScroll, items, handleItemClick]
    )

    const renderHorizontalNavigation = useCallback(
      ({ selectedItemId }: { selectedItemId: string }) => {
        return (
          <div ref={setRefs} className="relative w-full">
            <HorizontalNavigation
              {...horizontalNavProps}
              selectedItemId={selectedItemId}
            />
            {stuck &&
              typeof window !== 'undefined' &&
              createPortal(
                <div
                  className="fixed z-[3] hidden w-full lg:block"
                  style={{ top: `${topOffset - 1}px` }}
                >
                  <HorizontalNavigation
                    {...horizontalNavProps}
                    selectedItemId={selectedItemId}
                    className="rounded-t-none"
                  />
                </div>,
                document.body
              )}
          </div>
        )
      },
      [stuck, topOffset, horizontalNavProps, setRefs]
    )

    return (
      <React.Fragment>
        {!hideCategoriesNavigation &&
          (showHorizontalNav ? (
            <CategoriesNavigationWrapper {...navWrapperProps}>
              {renderHorizontalNavigation}
            </CategoriesNavigationWrapper>
          ) : (
            <CategoriesNavigationWrapper {...navWrapperProps}>
              {({ selectedItemId }) => (
                <div
                  ref={(node) => {
                    setRefs(node)
                  }}
                  className="category-navigation hidden w-full max-w-[25%] lg:block"
                >
                  <VerticalNavigation
                    {...verticalNavProps}
                    selectedItemId={selectedItemId}
                  />
                </div>
              )}
            </CategoriesNavigationWrapper>
          ))}
        {!hideCategoriesNavigation && (
          <CategoriesNavigationWrapper {...navWrapperProps}>
            {({ selectedItemId }) =>
              createPortal(
                <div
                  className={cn(
                    'text-body bg-container fixed top-[56px] z-10 flex w-full max-w-[100vw] items-center justify-between px-2 py-2 md:top-[83px] lg:hidden',
                    stuck ? 'visible' : 'invisible'
                  )}
                >
                  <P className="m-0 max-w-[80%] text-wrap">
                    {items?.find((item) => item.id === selectedItemId)?.name ||
                      items?.[0]?.name}
                  </P>
                  <DropdownMenu
                    open={isOpenDropdown}
                    onOpenChange={(isOpen) => setIsOpenDropdown(isOpen)}
                  >
                    <DropdownMenuTrigger className="flex items-center text-nowrap outline-0">
                      {t('More')}
                      <div className={isOpenDropdown ? 'rotate-180' : ''}>
                        <ChevronDownIcon className="h-4 w-4" />
                      </div>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      {items?.map((item) => (
                        <DropdownMenuItem key={item.id} className="text-body">
                          <a
                            href={item.href}
                            onClick={(e) => {
                              handleItemClick(e, item.id)
                              setIsOpenDropdown(false)
                            }}
                          >
                            {item.name}
                          </a>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>,
                document.body
              )
            }
          </CategoriesNavigationWrapper>
        )}
        <Column sm={12} lg={productSectionSize} className="px-0">
          <div
            className={cn(
              'relative pl-0',
              !hideCategoriesNavigation ? 'mt-0' : '',
              !hideCategoriesNavigation && !showHorizontalNav ? 'lg:pl-6' : ''
            )}
          >
            <div ref={setRefs} data-testid="categoriesNavigation.sensor">
              <div
                className="absolute h-px w-px"
                style={{ top: `${topOffset * -1}px` }}
              />
            </div>
            <NotificationBanner />
            <div ref={productsRef}>{children}</div>
          </div>
        </Column>
      </React.Fragment>
    )
  }
)

CategoriesNavigation.displayName = 'CategoriesNavigation'

export default CategoriesNavigation
